import { createContext, useContext, useEffect, useState } from "react";
import Backend from "../utils/api";
import { isKioskPath } from "../App/kioskRoutes";

/** Gates the redesigned design-system screens (the dashboard first). */
export const FRONTEND_V2_BETA = "frontend-v2-beta";

const FeatureFlagsContext = createContext({ flags: {}, ready: true });

/*
 * Loads the signed-in user's feature flags once per user. Flags are off until
 * loaded, after any failure, for signed-out users, and on kiosk screens (kiosk
 * sessions have no user to evaluate). `ready` tells gated routes when it is
 * safe to decide, so neither the gated page nor its fallback flashes.
 */
export function FeatureFlagsProvider({ user, sessionStatus, children }) {
  const uid = user?.UID;
  const shouldLoad =
    sessionStatus === "authenticated" &&
    uid !== undefined &&
    uid !== null &&
    !isKioskPath(window.location.pathname);
  const [loaded, setLoaded] = useState({ uid: null, flags: {} });

  useEffect(() => {
    if (!shouldLoad) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => Backend.getFeatureFlags())
      .then((res) => (res && res.status === 200 && res.flags ? res.flags : {}))
      .catch(() => ({}))
      .then((flags) => {
        if (!cancelled) setLoaded({ uid, flags });
      });
    return () => {
      cancelled = true;
    };
  }, [shouldLoad, uid]);

  let value;
  if (shouldLoad) {
    const current = loaded.uid === uid;
    value = { flags: current ? loaded.flags : {}, ready: current };
  } else {
    value = { flags: {}, ready: sessionStatus !== "checking" };
  }
  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

/** `{ enabled, ready }` for one flag; only a loaded `true` enables it. */
export function useFeatureFlag(name) {
  const { flags, ready } = useContext(FeatureFlagsContext);
  return { enabled: ready && flags[name] === true, ready };
}
